import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './SiteMap.module.css';

export interface ISiteMapOverlayHostProps {
  expanded: boolean;
  onToggleExpanded: () => void;
  /** The card element itself — built by `SiteMap.tsx`, this host only owns
   *  where it renders (see below), not what it renders. */
  children: ReactNode;
}

/**
 * Places `<SiteMap>`'s card where the fullscreen overlay needs it to be —
 * split out of `SiteMap.tsx` purely to keep that file under the 600-line
 * soft limit (quality gate), not because either half is independently
 * reusable.
 *
 * `.content` (App.module.css) is `position: fixed` — its own stacking
 * context at z-index:auto, painted BELOW the sidebar/topbar's own
 * `position: fixed; z-index: 20` no matter how high a DESCENDANT's z-index
 * goes (local z-index only wins WITHIN an ancestor's stacking context, never
 * past it). So a fixed full-viewport card mounted in-flow inside `.content`
 * can never cover the chrome — while expanded, it portals straight to
 * `<body>` instead, same reasoning as `ui/Drawer` (Drawer.tsx). Collapsed,
 * it renders in place, an ordinary flow child of LogisticsPage's row.
 *
 * The backdrop is a SIBLING of the card, not a wrapper — it dims the WHOLE
 * viewport (sidebar/topbar included) while the card sits inset 16px within
 * it (SiteMap.module.css). Only rendered while expanded; a click on it
 * collapses, same as Esc in view mode (`useSiteMapOverlay`).
 */
export function SiteMapOverlayHost({ expanded, onToggleExpanded, children }: ISiteMapOverlayHostProps) {
  const overlay = expanded ? (
    <>
      <div className={styles.backdrop} data-testid="site-map-backdrop" onClick={onToggleExpanded} />
      {children}
    </>
  ) : (
    children
  );
  return expanded ? createPortal(overlay, document.body) : overlay;
}
